import z from "zod";
import {
  BookkeepingAccountIdScheme,
  IntegrationIdScheme,
  PaymentMethodIdScheme,
} from "../ids";
import { PaymentMethodType } from "../enums/PaymentMethodType";
import { InvoiceCustomerDisplayField } from "../enums/InvoiceCustomerDisplayField";
import {
  PAYMENT_LINK_MAX_VALIDITY_DAYS,
  PaymentLinkPaymentMethodScheme,
} from "../paymentLink";

const PaymentMethodBaseScheme = z.object({
  id: PaymentMethodIdScheme,
  name: z.string(),
  type: z.enum(PaymentMethodType),
  bookkeepingAccountId: BookkeepingAccountIdScheme.nullable().optional(),
  color: z.string().nullable().optional(),
});

const PaymentMethodSwishCompanyScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.SWISH_COMPANY),
  swishNumber: z.string(),
  messageToCustomer: z.string().optional(),
});

const PaymentMethodSwishCommerceScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.SWISH_COMMERCE),
  swishNumber: z.string(),
  messageToCustomer: z.string().optional(),
});

const PaymentMethodNetsMockScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.NETS_MOCK),
});

const PaymentMethodNetsTerminalScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.NETS_TERMINAL),
  integrationId: IntegrationIdScheme,
  terminalId: z.string(),
});

const PaymentMethodCashScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.CASH),
  openCashDrawer: z.boolean().optional(),
  useChangeCalculator: z.boolean().optional(),
});

const PaymentMethodWestpayCloudScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.WESTPAY_CLOUD),
  terminalId: z.string(),
  isUAT: z.boolean().optional(),
  connectionId: z.string().optional(),
  isLinked: z.boolean().optional(),
  tipOnScreen: z.boolean().optional(),
  integrationId: IntegrationIdScheme,
});

const PaymentMethodVivaScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.VIVA),
  isFastRefundEnabled: z.boolean().optional(),
  integrationId: IntegrationIdScheme,
  multiMerchantId: z.string().optional(),
});

const PaymentMethodVivaCloudScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.VIVA_CLOUD),
  integrationId: IntegrationIdScheme,
  terminalId: z.string(),
  multiMerchantId: z.string().optional(),
});

const PaymentMethodCreditNoteScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.CREDIT_NOTE),
  askForAmount: z.boolean().optional(),
});

const PaymentMethodGiftCardScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.GIFT_CARD),
  askForAmount: z.boolean().optional(),
});

const PaymentMethodSoftpayScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.SOFTPAY),
  integrationId: IntegrationIdScheme,
  softpayStoreId: z.string().optional(),
});

const PaymentMethodOtherScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.OTHER),
  openCashDrawer: z.boolean().optional(),
  askForAmount: z.boolean().optional(),
});

const PaymentMethodInvoiceScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.INVOICE),
  integrationId: IntegrationIdScheme.nullable().optional(),
  emailDocumentToStore: z.boolean().optional(),
  paymentDuration: z.number().optional(), // in days
  printSignatureReceipt: z.boolean().optional(),
  documentHeaderText: z.string().optional(),
  customerSecondaryField: z.enum(InvoiceCustomerDisplayField).optional(),
  customerThirdField: z.enum(InvoiceCustomerDisplayField).optional(),
});

const PaymentMethodOrderScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.ORDER),
  integrationId: IntegrationIdScheme.nullable().optional(),
  emailDocumentToStore: z.boolean().optional(),
  printSignatureReceipt: z.boolean().optional(),
  documentHeaderText: z.string().optional(),
  includeOrderDate: z.boolean().optional(),
  customerSecondaryField: z.enum(InvoiceCustomerDisplayField).optional(),
  customerThirdField: z.enum(InvoiceCustomerDisplayField).optional(),
});

const PaymentMethodMarketpayCloudScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.MARKETPAY_CLOUD),
  terminalId: z.string(),
  integrationId: IntegrationIdScheme,
});

const PaymentMethodMarketpayLocalScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.MARKETPAY_LOCAL),
  ipAddress: z.string(),
  integrationId: IntegrationIdScheme,
});

const PaymentMethodMarketpaySdkScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.MARKETPAY_SDK),
  integrationId: IntegrationIdScheme,
});

const PaymentMethodPaymentLinkScheme = PaymentMethodBaseScheme.extend({
  type: z.literal(PaymentMethodType.PAYMENT_LINK),
  linkPaymentMethods: z.array(PaymentLinkPaymentMethodScheme),
  validityDays: z.number().int().positive().max(PAYMENT_LINK_MAX_VALIDITY_DAYS),
  fiscalize: z.boolean().optional(),
});

export const PaymentMethodDBScheme = z.discriminatedUnion("type", [
  PaymentMethodSwishCompanyScheme,
  PaymentMethodCashScheme,
  PaymentMethodNetsMockScheme,
  PaymentMethodNetsTerminalScheme,
  PaymentMethodVivaScheme,
  PaymentMethodVivaCloudScheme,
  PaymentMethodCreditNoteScheme,
  PaymentMethodGiftCardScheme,
  PaymentMethodWestpayCloudScheme,
  PaymentMethodSwishCommerceScheme,
  PaymentMethodSoftpayScheme,
  PaymentMethodOtherScheme,
  PaymentMethodInvoiceScheme,
  PaymentMethodOrderScheme,
  PaymentMethodMarketpayCloudScheme,
  PaymentMethodMarketpayLocalScheme,
  PaymentMethodMarketpaySdkScheme,
  PaymentMethodPaymentLinkScheme,
]);

const knownPaymentMethodTypes = new Set<unknown>(
  PaymentMethodDBScheme.options.map((option) => option.shape.type.value),
);

export const hasKnownPaymentMethodType = (paymentMethod: { type?: unknown }) =>
  knownPaymentMethodTypes.has(paymentMethod.type);

export const RestGetPaymentMethod = PaymentMethodDBScheme;
export const RestCreatePaymentMethod = z.discriminatedUnion("type", [
  PaymentMethodSwishCompanyScheme.omit({ id: true }),
  PaymentMethodCashScheme.omit({ id: true }),
  PaymentMethodNetsMockScheme.omit({ id: true }),
  PaymentMethodNetsTerminalScheme.omit({ id: true }),
  PaymentMethodVivaScheme.omit({ id: true }),
  PaymentMethodVivaCloudScheme.omit({ id: true }),
  PaymentMethodCreditNoteScheme.omit({ id: true }),
  PaymentMethodGiftCardScheme.omit({ id: true }),
  PaymentMethodWestpayCloudScheme.omit({ id: true }),
  PaymentMethodSwishCommerceScheme.omit({ id: true }),
  PaymentMethodSoftpayScheme.omit({ id: true }),
  PaymentMethodOtherScheme.omit({ id: true }),
  PaymentMethodInvoiceScheme.omit({ id: true }),
  PaymentMethodOrderScheme.omit({ id: true }),
  PaymentMethodMarketpayCloudScheme.omit({ id: true }),
  PaymentMethodMarketpayLocalScheme.omit({ id: true }),
  PaymentMethodMarketpaySdkScheme.omit({ id: true }),
  PaymentMethodPaymentLinkScheme.omit({ id: true }),
]);

export const RestUpdatePaymentMethod = z.discriminatedUnion("type", [
  PaymentMethodSwishCompanyScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
  PaymentMethodCashScheme.omit({ id: true }).partial().required({ type: true }),
  PaymentMethodNetsMockScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
  PaymentMethodNetsTerminalScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
  PaymentMethodVivaScheme.omit({ id: true }).partial().required({ type: true }),
  PaymentMethodVivaCloudScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
  PaymentMethodCreditNoteScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
  PaymentMethodGiftCardScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
  PaymentMethodWestpayCloudScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
  PaymentMethodSwishCommerceScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
  PaymentMethodSoftpayScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
  PaymentMethodOtherScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
  PaymentMethodInvoiceScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
  PaymentMethodOrderScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
  PaymentMethodMarketpayCloudScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
  PaymentMethodMarketpayLocalScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
  PaymentMethodMarketpaySdkScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
  PaymentMethodPaymentLinkScheme.omit({ id: true })
    .partial()
    .required({ type: true }),
]);

export type DBPaymentMethod = z.infer<typeof PaymentMethodDBScheme>;
export type RestPaymentMethod = z.infer<typeof RestGetPaymentMethod>;
export type RestCreatePaymentMethod = z.infer<typeof RestCreatePaymentMethod>;
export type RestUpdatePaymentMethod = z.infer<typeof RestUpdatePaymentMethod>;

export type PaymentMethodCash = z.infer<typeof PaymentMethodCashScheme>;
export type PaymentMethodNetsMock = z.infer<typeof PaymentMethodNetsMockScheme>;
export type PaymentMethodNetsTerminal = z.infer<
  typeof PaymentMethodNetsTerminalScheme
>;
export type PaymentMethodViva = z.infer<typeof PaymentMethodVivaScheme>;
export type PaymentMethodVivaCloud = z.infer<
  typeof PaymentMethodVivaCloudScheme
>;
export type PaymentMethodCreditNote = z.infer<
  typeof PaymentMethodCreditNoteScheme
>;
export type PaymentMethodGiftCard = z.infer<typeof PaymentMethodGiftCardScheme>;
export type PaymentMethodWestpayCloud = z.infer<
  typeof PaymentMethodWestpayCloudScheme
>;
export type PaymentMethodSwishCommerce = z.infer<
  typeof PaymentMethodSwishCommerceScheme
>;
export type PaymentMethodSwishCompany = z.infer<
  typeof PaymentMethodSwishCompanyScheme
>;
export type PaymentMethodSoftpay = z.infer<typeof PaymentMethodSoftpayScheme>;
export type PaymentMethodOther = z.infer<typeof PaymentMethodOtherScheme>;
export type PaymentMethodInvoice = z.infer<typeof PaymentMethodInvoiceScheme>;
export type PaymentMethodOrder = z.infer<typeof PaymentMethodOrderScheme>;
export type PaymentMethodMarketpayCloud = z.infer<
  typeof PaymentMethodMarketpayCloudScheme
>;
export type PaymentMethodMarketpayLocal = z.infer<
  typeof PaymentMethodMarketpayLocalScheme
>;
export type PaymentMethodMarketpaySdk = z.infer<
  typeof PaymentMethodMarketpaySdkScheme
>;
export type PaymentMethodPaymentLink = z.infer<
  typeof PaymentMethodPaymentLinkScheme
>;
